import React, { useState } from "react";
import { FileUp, AlertCircle } from "lucide-react";
import { usePdfExtractor } from "../hooks/usePDFExtractor";

interface MaterialsAndInfoSectionProps {
  isDark: boolean;
  additionalInfo: string;
  onAdditionalInfoChange: (value: string) => void;
  onPdfTextExtracted: (text: string) => void;
}

export const MaterialsAndInfoSection: React.FC<
  MaterialsAndInfoSectionProps
> = ({
  isDark,
  additionalInfo,
  onAdditionalInfoChange,
  onPdfTextExtracted,
}) => {
  const { extractAll, loading, error } = usePdfExtractor();
  const [fileName, setFileName] = useState<string | null>(null);

  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const result = await extractAll(file);
      setFileName(result.filename || file.name);
      onPdfTextExtracted(result.text);
      console.log("[UI] ✓ PDF processado com sucesso");
    } catch (err) {
      console.error("[UI] ✗ Erro ao processar PDF:", err);
    }
  };

  return (
    <div className="space-y-4">
      <div className="col-span-full bg-(--card-bg) border border-(--border-color) rounded-2xl p-6 shadow-sm mb-2">
        <label className="block text-xs font-bold uppercase tracking-wider mb-4 text-(--text-tertiary)">
          Informações Adicionais (Opcional)
        </label>
        <textarea
          value={additionalInfo}
          onChange={(e) => onAdditionalInfoChange(e.target.value)}
          placeholder="Ex: 'Focar em equações do segundo grau', 'Nível de dificuldade médio', 'Incluir problemas contextualizados'..."
          className="w-full h-32 px-4 py-3 rounded-xl border border-(--border-color) focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-(--primary-color) transition-all bg-(--card-bg) text-(--text-primary) font-medium placeholder:italic placeholder:font-normal"
        />
        <div className="flex items-center gap-2 mt-3 text-(--text-tertiary)">
          <AlertCircle className="w-3.5 h-3.5" />
          <p className="text-[11px] font-medium">
            Use para orientar a IA sobre o tom e foco da atividade.
          </p>
        </div>
      </div>

      <div className="col-span-full bg-(--card-bg) border border-(--border-color) rounded-2xl p-6 shadow-sm">
        <label className="block text-xs font-bold uppercase tracking-wider mb-4 text-(--text-tertiary)">
          📄 Material de Base (Opcional)
        </label>

        <div className="space-y-4">
          <label className="block cursor-pointer group">
            <div
              className="border-2 border-dashed rounded-xl p-8 text-center transition-all group-hover:bg-blue-50/50 group-hover:border-(--primary-color)"
              style={{
                borderColor: "var(--border-color)",
                backgroundColor: "var(--bg-secondary)",
              }}
            >
              <div className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center mx-auto mb-4 border border-(--border-color) group-hover:border-(--primary-color)">
                <FileUp
                  className="w-6 h-6 transition-colors group-hover:text-(--primary-color)"
                  style={{ color: "var(--text-tertiary)" }}
                />
              </div>
              <p className="text-sm font-bold text-(--text-secondary) mb-1">
                {loading
                  ? "Processando documento..."
                  : fileName
                    ? fileName
                    : "Upload de material de apoio"}
              </p>
              <p className="text-xs text-(--text-tertiary)">
                {fileName
                  ? "Arquivo pronto para uso"
                  : "Clique ou arraste um PDF (Max 50MB)"}
              </p>
            </div>
            <input
              type="file"
              accept=".pdf"
              onChange={handlePdfUpload}
              disabled={loading}
              className="hidden"
            />
          </label>

          {error && (
            <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-100 animate-in fade-in slide-in-from-top-1">
              <div className="p-1 rounded-full bg-red-100 text-red-600">
                <AlertCircle className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold text-red-900 uppercase tracking-wider mb-0.5">
                  Falha no processamento
                </p>
                <p className="text-xs text-red-700 font-medium">{error}</p>
              </div>
            </div>
          )}

          {fileName && !error && (
            <div className="flex items-start gap-3 p-4 rounded-xl bg-green-50 border border-green-100 animate-in fade-in slide-in-from-top-1">
              <div className="p-1 rounded-full bg-green-100 text-green-600">
                <FileUp className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold text-green-900 uppercase tracking-wider mb-0.5">
                  Material vinculado
                </p>
                <p className="text-xs text-green-700 font-medium">
                  A IA usará o conteúdo deste PDF como base para as questões.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
